import { useState } from 'react'
import { Link } from 'react-router'
import { QRCodeSVG } from 'qrcode.react'
import { api, ApiError } from '../../lib/api'
import { useCountdown } from '../../lib/clock'
import { useDraftContext } from '../../state/DraftProvider'
import BoardPanel from '../draft/BoardPanel'
import CopyLink from '../CopyLink'
import ForcePickModal from './ForcePickModal'
import type { DraftResponse, DraftState, DraftStatus } from '../../types/protocol'

interface RunOptions {
  method?: 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
}

type Run = (path: string, options?: RunOptions) => Promise<void>

// Everything the commissioner does to a running draft, and nothing else. This
// is the whole surface behind /control#<token> — where the Start button lands —
// laid out in the order draft night reaches for it: the clock and the four
// flow controls, then the teams, then the screens to put in front of people,
// then a condensed board so you can see what you are changing.
//
// The flow controls are deliberately exactly these — undo the latest pick,
// force a pick, skip, backfill, pause, autopick, release a claim. There is no
// editing of a pick in place, and nothing here edits settings, teams, or
// rankings: the engine guards those to setup, so they stay on /setup.
export default function ControlPanel({ commissionerToken }: { commissionerToken: string }) {
  const { draft, players, loading, error } = useDraftContext()
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [forcePickOpen, setForcePickOpen] = useState(false)

  if (loading) return <p className="text-ink-lo">Loading…</p>
  if (error) return <p className="text-crit">{error}</p>
  if (!draft) return <p className="text-ink-lo">No draft data</p>

  async function run(path: string, options: RunOptions = {}) {
    setBusy(true)
    setActionError(null)
    try {
      await api<DraftResponse>(path, {
        method: options.method ?? 'POST',
        body: options.body,
        commissionerToken,
      })
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'That did not work')
    } finally {
      setBusy(false)
    }
  }

  const live = draft.status === 'live'
  const paused = draft.status === 'paused'
  const complete = draft.status === 'complete'
  const madeCount = draft.picks.filter((pick) => pick.status === 'made').length
  const skippedCount = draft.picks.filter((pick) => pick.status === 'skipped').length

  return (
    <div className="space-y-6">
      <section className="panel p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <OnClock draft={draft} />
          <StatusChip status={draft.status} />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {paused ? (
            <Control label="Resume" tone="primary" disabled={busy}
                     onClick={() => void run(`/drafts/${draft.join_code}/resume`)} />
          ) : (
            <Control label="Pause" disabled={busy || !live}
                     onClick={() => void run(`/drafts/${draft.join_code}/pause`)} />
          )}
          <Control label="Force pick" tone="primary" disabled={busy || complete}
                   onClick={() => setForcePickOpen(true)} />
          <Control label="Skip this pick" disabled={busy || !live}
                   onClick={() => void run(`/drafts/${draft.join_code}/skip`)} />
          <Control label="Undo last pick" disabled={busy || madeCount === 0}
                   onClick={() => void run(`/drafts/${draft.join_code}/undo`)} />
        </div>

        {skippedCount > 0 && (
          <p className="mt-3 text-sm text-warn">
            {skippedCount} pick{skippedCount === 1 ? ' was' : 's were'} skipped — backfill{' '}
            {skippedCount === 1 ? 'it' : 'them'} from Force pick.
          </p>
        )}
        {actionError && <p className="mt-3 text-sm text-crit">{actionError}</p>}
      </section>

      <TeamControls draft={draft} busy={busy} onRun={run} />

      <ScreensAndLinks draft={draft} commissionerToken={commissionerToken} />

      <BoardPanel draft={draft} players={players} />

      {forcePickOpen && (
        <ForcePickModal
          draft={draft}
          players={players}
          commissionerToken={commissionerToken}
          onClose={() => setForcePickOpen(false)}
        />
      )}
    </div>
  )
}

function Control({
  label,
  onClick,
  disabled,
  tone,
}: {
  label: string
  onClick: () => void
  disabled: boolean
  tone?: 'primary'
}) {
  const base = 'rounded px-3 py-1.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40'
  const colors = tone === 'primary'
    ? 'bg-signal text-ink hover:bg-signal-dim'
    : 'border border-line-strong text-ink-hi hover:bg-surface-3'

  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${colors}`}>
      {label}
    </button>
  )
}

// Only `paused` carries an accent, and it is signal — chrome, which is what
// signal is for. The other three states stay neutral on purpose: `turn` means
// "it's you" and nothing else anywhere, and `warn` belongs to the clock alone,
// so neither is available to a status chip however well the hue would fit.
// What separates the states here is the word, not the colour.
const STATUS_STYLES: Record<DraftStatus, string> = {
  setup: 'border-line-strong text-ink-lo',
  live: 'border-line-strong text-ink-mid',
  // The one state waiting on the commissioner, tinted to match the Resume
  // button below it — the chip and the thing that clears it are the same hue.
  paused: 'border-signal/30 bg-signal/10 text-signal',
  complete: 'border-line-strong text-ink-lo',
}

// Answers "why is nothing happening" at a glance: a paused draft and a draft
// waiting on a slow owner read identically off the clock alone.
function StatusChip({ status }: { status: DraftStatus }) {
  return (
    <span
      className={`shrink-0 rounded-full border px-3 py-1 font-mono text-[10px] font-semibold tracking-[0.18em] uppercase ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  )
}

function OnClock({ draft }: { draft: DraftState }) {
  const deadlineMs = draft.deadline_at ? new Date(draft.deadline_at).getTime() : null
  const { remainingMs, expired } = useCountdown(deadlineMs)

  const pick = draft.picks.find((p) => p.overall === draft.current_pick_overall)
  const team = pick ? draft.teams.find((t) => t.id === pick.team_id) : null

  let clock = '--:--'
  if (draft.status === 'paused') {
    const secs = draft.paused_remaining_ms != null
      ? Math.ceil(draft.paused_remaining_ms / 1000)
      : draft.pick_seconds
    clock = `PAUSED (${secs}s)`
  } else if (draft.status === 'complete') {
    clock = 'Complete'
  } else if (draft.status === 'live' && deadlineMs == null) {
    clock = 'EXPIRED'
  } else if (expired) {
    clock = '0:00'
  } else if (remainingMs != null) {
    const total = Math.ceil(remainingMs / 1000)
    clock = `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`
  }

  return (
    <div>
      <p className="text-sm text-ink-mid">
        {pick && team ? (
          <>
            On the clock: <span className="font-semibold text-ink-hi">{team.name}</span> (R
            {pick.round}.{pick.slot})
          </>
        ) : (
          `Draft is ${draft.status}`
        )}
      </p>
      <p className="text-2xl font-bold tabular-nums">{clock}</p>
    </div>
  )
}

function TeamControls({
  draft,
  busy,
  onRun,
}: {
  draft: DraftState
  busy: boolean
  onRun: Run
}) {
  return (
    <section className="panel p-5">
      <h2 className="eyebrow">Teams</h2>
      <ul className="mt-3 divide-y divide-line">
        {draft.teams.map((team) => (
          <li key={team.id} className="flex flex-wrap items-center gap-3 py-2">
            <span className="w-6 shrink-0 text-xs tabular-nums text-ink-lo">
              {team.draft_position ?? '–'}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm">
              {team.name}
              {team.owner_name && <span className="ml-2 text-xs text-ink-lo">{team.owner_name}</span>}
            </span>
            <label className="flex shrink-0 items-center gap-1.5 text-xs text-ink-mid">
              <input
                type="checkbox"
                checked={team.autopick_enabled}
                disabled={busy}
                onChange={(event) =>
                  void onRun(`/drafts/${draft.join_code}/teams/${team.id}/autopick`, {
                    method: 'PATCH',
                    body: { enabled: event.target.checked },
                  })
                }
              />
              Autopick
            </label>
            {team.claimed ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void onRun(`/drafts/${draft.join_code}/teams/${team.id}/claim`, { method: 'DELETE' })
                }
                className="shrink-0 text-xs text-ink-mid underline-offset-2 hover:underline disabled:opacity-40"
              >
                Release claim
              </button>
            ) : (
              <span className="shrink-0 text-xs text-ink-lo">Unclaimed</span>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-ink-lo">
        Releasing a claim lets a new device join as that team, and turns its autopick off.
      </p>
    </section>
  )
}

// Every other view of this draft, from the one page that holds the token. The
// join QR outlives setup on purpose: someone always turns up late, and after a
// released claim the same code is how that device gets back in.
function ScreensAndLinks({
  draft,
  commissionerToken,
}: {
  draft: DraftState
  commissionerToken: string
}) {
  const joinUrl = `${window.location.origin}/join/${draft.join_code}`
  const boardUrl = `${window.location.origin}/board/${draft.join_code}`
  const lotteryUrl = `${window.location.origin}/lottery/${draft.join_code}`

  return (
    <section className="panel p-5">
      <h2 className="eyebrow">Screens &amp; links</h2>
      <div className="mt-4 flex flex-col gap-5 sm:flex-row">
        {/* Deliberately un-themed, like JoinQr: a QR needs maximum luminance
            contrast to scan, so this stays white whatever the surface is. */}
        <div className="self-start rounded-lg bg-white p-3">
          <QRCodeSVG value={joinUrl} size={112} />
        </div>
        <div className="min-w-0 flex-1 space-y-4 text-sm">
          <div>
            <p className="text-ink-mid">
              Latecomers scan the code, or join with{' '}
              <span className="font-mono font-bold tracking-widest text-signal">
                {draft.join_code}
              </span>
              :
            </p>
            <CopyLink url={joinUrl} />
          </div>
          <CopyLink label="Put the board on the TV:" url={boardUrl} />
          {/* Only a draw that actually happened is worth linking to from here.
              This panel is post-setup, and the sweeper's query is scoped to
              setup — so a lottery_at with no lottery_revealed_at at this point
              is one that can never fire, and the page would say so. */}
          {draft.lottery_revealed_at && (
            <CopyLink label="The lottery replay:" url={lotteryUrl} />
          )}
          <div className="flex flex-wrap gap-x-5 gap-y-2 border-t border-line pt-4 text-sm text-ink-mid">
            <Link
              to={`/board/${draft.join_code}`}
              className="underline-offset-2 hover:text-ink-hi hover:underline"
            >
              Open the board →
            </Link>
            {/* The commissioner's own way into the pool. The draft room is the
                one page an owner and the commissioner both reach, so the token
                is what distinguishes them — carried in the fragment, which no
                browser puts in the request line. Draft.tsx reads #ct= and
                ignores ?ct= entirely, so the two have to agree or the
                commissioner silently arrives as a spectator. */}
            <Link
              to={`/draft/${draft.join_code}#ct=${encodeURIComponent(commissionerToken)}`}
              className="underline-offset-2 hover:text-ink-hi hover:underline"
            >
              Draft room →
            </Link>
            <Link
              to={`/results/${draft.join_code}`}
              className="underline-offset-2 hover:text-ink-hi hover:underline"
            >
              Results →
            </Link>
            {draft.lottery_revealed_at && (
              <Link
                to={`/lottery/${draft.join_code}`}
                className="underline-offset-2 hover:text-ink-hi hover:underline"
              >
                Lottery →
              </Link>
            )}
            {/* Outside the SPA's routes on purpose — a full navigation is what
                makes the browser download the file. */}
            <a
              href={`/drafts/${draft.join_code}/export`}
              className="underline-offset-2 hover:text-ink-hi hover:underline"
            >
              Export CSV ↓
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}
